import { useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import useDataset from "datasets/hooks/useDataset";
import useDatasetPermission from "datasets/hooks/useDatasetPermission";
import { removeDashboard } from "datasets/pages/dashboards/api/dashboardApi";
import useDatasetDashboards from "datasets/pages/dashboards/hooks/useDatasetDashboards";
import useReport from "datasets/pages/dashboards/hooks/useReport";
import { Loader2 } from "lucide-react";
import SigmaEmbed from "~/components/SigmaEmbed";
import useErrorToast from "~/contexts/error-toast";
import { trackEvent } from "~/utils/monitoring";

export const DatasetReport = () => {
  const { datasetId, reportId } = useParams();
  const { data: report, isLoading } = useReport();
  const { mutate: mutateDataset } = useDataset();
  const { mutate: mutateDashboards } = useDatasetDashboards();
  const hasEditPermission = useDatasetPermission("edit:catalog-report");
  const { showError } = useErrorToast();
  const navigate = useNavigate();

  useEffect(() => {
    if (datasetId && reportId) {
      trackEvent("dataset_dashboard_viewed", {
        dataset_id: datasetId,
        dashboard_id: reportId,
      });
    }
  }, [datasetId, reportId]);

  const handleClose = () => navigate("..", { relative: "path" });

  const handleRemove = async () => {
    try {
      await removeDashboard(datasetId!, reportId!);
      await Promise.all([mutateDataset(), mutateDashboards()]);
      handleClose();
    } catch (e: any) {
      showError(e.message ?? "Failed to remove dashboard");
    }
  };

  const messageHandler = async (e: MessageEvent) => {
    if (e.data.type === "workbook:ondelete") {
      await handleRemove();
    }
  };

  if (isLoading) {
    return (
      <div className="flex h-full min-h-[50vh] w-full items-center justify-center">
        <Loader2 className="size-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (!report) {
    return null;
  }

  return (
    <SigmaEmbed
      title={report.displayName}
      datasetId={datasetId}
      reportFqn={report.fullyQualifiedName}
      messageHandler={messageHandler}
      onClose={handleClose}
      allowEdits={hasEditPermission}
    />
  );
};
